/**
 * areaSubmitFlow — PURE orchestration of the resumable-area submit
 * (PR-ONB-PRECHECK-3, review #868 fix #2).
 *
 * Extracted from SummaryPage so the token-freshness contract is unit-testable
 * without rendering React. The submit MUST be self-sufficient: the candidate
 * may have verified their email in ANOTHER tab, so before reading the active
 * acceptance version / revision and POSTing the submit we:
 *
 *   1. flush the shared autosave (the draft IS the payload);
 *   2. reload the Firebase user (picks up the email_verified claim updated
 *      out-of-band);
 *   3. force a FRESH ID token (getIdToken(true)) — carries the refreshed claim;
 *   4. GET /area/me with THAT fresh token (revision + acceptanceActiveVersion);
 *   5. POST /area/request/submit with the SAME fresh token.
 *
 * The user never has to bounce through the Stato page's "Ho verificato
 * l'email" CTA first.
 *
 * The function is generic over its dependencies (no fetch, no Firebase import)
 * so the test can assert the exact call order + that GET me and POST submit
 * receive the identical fresh token.
 */

import type { AreaFailure, AreaMe, AreaSubmitOk } from '@/lib/areaApi';

export interface AreaSubmitDeps {
  /** Flush the shared autosave machine; resolves false when not clean. */
  flush: () => Promise<boolean>;
  /** firebaseUser.reload() — refreshes the local emailVerified claim. */
  reloadUser: () => Promise<void>;
  /** getIdToken(force). MUST be called with force=true here. */
  getToken: (force: boolean) => Promise<string | null>;
  /** GET /area/me with the given token. */
  getMe: (token: string) => Promise<{ ok: true; data: AreaMe } | AreaFailure>;
  /** POST /area/request/submit with the given token. */
  submit: (
    token: string,
    body: {
      expectedRevision: number;
      acceptanceConfirmed: true;
      acceptanceVersion: string;
    },
  ) => Promise<{ ok: true; data: AreaSubmitOk } | AreaFailure>;
}

export type AreaSubmitFlowResult =
  | { kind: 'OK'; data: AreaSubmitOk }
  | { kind: 'FLUSH_DIRTY' }
  | { kind: 'NO_SESSION' }
  | { kind: 'ME_UNAVAILABLE' }
  | { kind: 'EMAIL_NOT_VERIFIED' }
  | { kind: 'SUBMIT_FAILED'; failure: AreaFailure };

export async function performAreaSubmit(
  deps: AreaSubmitDeps,
): Promise<AreaSubmitFlowResult> {
  // 1. The draft is the payload — make sure the last edits are persisted.
  const clean = await deps.flush();
  if (!clean) return { kind: 'FLUSH_DIRTY' };

  // 2 + 3. Reload the Firebase user THEN force a fresh token, so an email
  // verified in another tab is reflected in the claim this submit carries.
  await deps.reloadUser();
  const token = await deps.getToken(true);
  if (!token) return { kind: 'NO_SESSION' };

  // 4. Read the active acceptance version + current revision with the fresh
  // token (same token the submit will use).
  const me = await deps.getMe(token);
  if (!me.ok || !me.data.request) return { kind: 'ME_UNAVAILABLE' };
  if (!me.data.account.emailVerified) return { kind: 'EMAIL_NOT_VERIFIED' };

  // 5. Submit with the SAME fresh token.
  const result = await deps.submit(token, {
    expectedRevision: me.data.request.draftRevision,
    acceptanceConfirmed: true,
    acceptanceVersion: me.data.acceptanceActiveVersion,
  });
  if (result.ok) return { kind: 'OK', data: result.data };
  return { kind: 'SUBMIT_FAILED', failure: result };
}
